<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import inboxesAPI from 'dashboard/api/inboxes';

// Use global axios which has auth interceptors
/* global axios */

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const WWEGJS_GATEWAY_URL = 'http://localhost:3060';
// Rails runs inside a Docker container — it can't reach the gateway via the
// browser-facing localhost URL above, so the stored channel gateway_url
// (used by backend send/sync calls) must use the Docker network hostname.
const WWEBJS_GATEWAY_BACKEND_URL = 'http://wwebjs-gateway:3060';
// Same reasoning as above, but for the webhook the gateway calls back into
// Rails with — the gateway container can't resolve the browser's own origin.
const WWEBJS_WEBHOOK_BASE_URL = 'http://wa-develop-rails-1:3000';

const sessionId = ref('');
const companyName = ref('');
const phoneNumber = ref('');
const qrCodeUrl = ref('');
const sessionStatus = ref('');
const isCreating = ref(false);
const isPollingQR = ref(false);

const accountId = computed(() => route.params.accountId);

const isDisconnected = computed(() => !sessionStatus.value || sessionStatus.value === 'disconnected');
const isQrReady = computed(() => sessionStatus.value === 'qr_ready');
const isConnected = computed(() => sessionStatus.value === 'connected');

async function createSession() {
  if (!phoneNumber.value) {
    useAlert('Phone number is required');
    return;
  }

  isCreating.value = true;

  try {
    const response = await fetch(`${WWEGJS_GATEWAY_URL}/api/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phone_number: phoneNumber.value.replace(/[^0-9]/g, ''),
        display_name: companyName.value || phoneNumber.value,
      }),
    });

    const data = await response.json();
    sessionId.value = data.session_id;
    sessionStatus.value = 'connecting';

    useAlert('Session created. Generating QR code...');
    startQRPolling();
  } catch (error) {
    useAlert(`Failed to create session: ${error.message}`);
  } finally {
    isCreating.value = false;
  }
}

function startQRPolling() {
  if (!sessionId.value) return;

  isPollingQR.value = true;

  const pollInterval = setInterval(async () => {
    try {
      const response = await fetch(
        `${WWEGJS_GATEWAY_URL}/api/sessions/${sessionId.value}`
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      console.log('Polling:', data.session_id, 'status:', data.status);

      // Connected — proceed
      if (data.status === 'connected') {
        clearInterval(pollInterval);
        isPollingQR.value = false;
        sessionStatus.value = 'connected';
        useAlert('WhatsApp connected! Creating inbox...');
        await createChatwootInbox();
        return;
      }

      // Still generating QR
      if (data.status === 'qr_ready' || data.status === 'connecting') {
        sessionStatus.value = data.status;

        // Also try to fetch QR image if available
        try {
          const qrResp = await fetch(
            `${WWEGJS_GATEWAY_URL}/api/sessions/${sessionId.value}/qr/base64`
          );
          if (qrResp.ok) {
            const qrData = await qrResp.json();
            if (qrData.qr_code) {
              qrCodeUrl.value = qrData.qr_code;
            }
          }
        } catch {
          // QR not available yet, that's ok
        }
      }

      // Error state
      if (data.status === 'error' || data.status === 'disconnected') {
        sessionStatus.value = data.status;
        if (data.error_message) {
          console.error('Session error:', data.error_message);
        }
      }
    } catch (error) {
      console.error('Polling error:', error.message);
    }
  }, 2000);

  // Stop polling after 5 minutes
  setTimeout(() => {
    clearInterval(pollInterval);
    isPollingQR.value = false;
    if (sessionStatus.value !== 'connected') {
      useAlert('QR scan timeout. Please try again.');
      sessionStatus.value = 'disconnected';
    }
  }, 5 * 60 * 1000);
}

async function createChatwootInbox() {
  try {
    const inboxName = companyName.value || `WhatsApp ${phoneNumber.value}`;
    console.log('Creating inbox:', { name: inboxName, session_id: sessionId.value, type: 'wwebjs' });

    // Use the global axios which has auth interceptors
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/inboxes`,
      {
        name: inboxName,
        channel: {
          type: 'wwebjs',
          session_id: sessionId.value,
          phone_number: phoneNumber.value.replace(/[^0-9]/g, ''),
          gateway_url: WWEBJS_GATEWAY_BACKEND_URL,
          status: 'connected',
          display_name: companyName.value,
        },
      }
    );

    const data = response.data;
    console.log('Inbox created:', data);
    useAlert('Inbox created successfully!');

    // Link the session to this inbox in the gateway
    await fetch(
      `${WWEGJS_GATEWAY_URL}/api/sessions/${sessionId.value}/link-chatwoot`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatwoot_inbox_id: data.id,
          webhook_url: `${WWEBJS_WEBHOOK_BASE_URL}/webhooks/wwebjs/${data.id}`,
        }),
      }
    );

    router.push({
      name: 'settings_inbox_list',
      params: { accountId: accountId.value },
    });
  } catch (error) {
    console.error('createChatwootInbox error:', error.response?.data || error.message);
    useAlert(`Failed to create inbox: ${error.response?.data?.message || error.message}`);
  }
}

async function disconnectSession() {
  if (!sessionId.value) return;

  try {
    await fetch(`${WWEGJS_GATEWAY_URL}/api/sessions/${sessionId.value}/disconnect`, {
      method: 'POST',
    });

    sessionStatus.value = 'disconnected';
    qrCodeUrl.value = '';
    useAlert('Session disconnected');
  } catch (error) {
    useAlert(`Failed to disconnect: ${error.message}`);
  }
}
</script>

<template>
  <div class="space-y-6">
    <div>
      <h2 class="text-lg font-semibold text-n-slate-12">
        wwebjs.dev — WhatsApp Web JS
      </h2>
      <p class="text-sm text-n-slate-11 mt-1">
        Connect via WhatsApp Web JS library (Baileys engine). Scan QR code to authenticate.
      </p>
    </div>

    <!-- Session Creation Form -->
    <div v-if="isDisconnected && !sessionId" class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          Display Name
        </label>
        <input
          v-model="companyName"
          type="text"
          class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-n-brand"
          placeholder="e.g. Sales Team 1"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          WhatsApp Phone Number
        </label>
        <input
          v-model="phoneNumber"
          type="text"
          class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-n-brand"
          placeholder="e.g. 6281234567890"
        />
        <p class="text-xs text-n-slate-10 mt-1">
          Include country code, no + or spaces
        </p>
      </div>

      <button
        class="px-4 py-2 bg-n-brand text-white rounded-lg text-sm font-medium hover:bg-n-brand/90 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isCreating || !phoneNumber"
        @click="createSession"
      >
        <span v-if="isCreating">Creating...</span>
        <span v-else>Create & Generate QR</span>
      </button>
    </div>

    <!-- QR Code Display -->
    <div v-if="sessionId && isQrReady" class="space-y-4">
      <div class="flex flex-col items-center p-6 bg-white rounded-xl border border-n-weak">
        <img
          v-if="qrCodeUrl"
          :src="qrCodeUrl"
          alt="WhatsApp QR Code"
          class="w-64 h-64"
        />
        <p v-else class="text-sm text-n-slate-11 animate-pulse">
          Generating QR code...
        </p>
        <p class="text-sm font-medium text-n-slate-12 mt-4">
          Scan with WhatsApp on your phone
        </p>
        <p class="text-xs text-n-slate-10 mt-1">
          Linked Devices → Link a Device
        </p>
      </div>

      <p v-if="isPollingQR" class="text-xs text-n-slate-10 text-center">
        Waiting for scan... (polling every 2s)
      </p>
    </div>

    <!-- Connected Status -->
    <div v-if="isConnected" class="space-y-4">
      <div class="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
        <div class="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
          <svg class="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <p class="text-sm font-semibold text-emerald-800">Connected</p>
          <p class="text-xs text-emerald-600">
            Creating inbox in Chatwoot...
          </p>
        </div>
      </div>
    </div>

    <!-- Info -->
    <div class="p-4 bg-n-slate-2 rounded-lg border border-n-weak">
      <p class="text-xs text-n-slate-11">
        Powered by
        <a
          href="https://wwebjs.dev"
          target="_blank"
          rel="noopener noreferrer"
          class="underline text-n-brand hover:text-n-brand/80"
        >
          wwebjs.dev
        </a>
        (Baileys engine)
      </p>
    </div>
  </div>
</template>
